import { Box, IconButton, Icon, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { FiMoreHorizontal } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  useCompleteSourcingEventMutation,
  useDeleteSourcingEventMutation,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  useReopenCompletedSourcingEventMutation,
  type SourcingEventDto,
} from "../../autogen/bff-api";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { useApiError } from "../../common/errors/useApiError";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { urls } from "../../urls";
import { isDesignContest } from "../sourcing-events/buyer/wizard/utils/designContest";
import { useCopyProcurement } from "../sourcing-events/useCopyProcurement";
import { EventChangeEditor } from "./changes/EventChangeEditor";
import { useChangeFlow } from "./changes/useChangeFlow";
import { everyScoreIn } from "./evaluation/evaluationCompleteness";
import { useCompleteEvaluationDescription } from "./evaluation/useCompleteEvaluationDescription";
import { useRoundSelection } from "./rounds/RoundSelectionContext";
import { supportsNegotiationRounds } from "./rounds/supportsNegotiationRounds";
import { useStartRound } from "./rounds/useStartRound";
import { publishedBidDeadline } from "./qualification/stage";
import { MissingContractsNotice } from "./MissingContractsNotice";
import { useCancelOrCloseProcurement } from "./useCancelOrCloseProcurement";

export const EventLifecycleMenu = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();
  const completeEvaluationDescription = useCompleteEvaluationDescription(event);

  const [completeEvent] = useCompleteSourcingEventMutation();
  const [reopenEvent] = useReopenCompletedSourcingEventMutation();
  const [deleteEvent] = useDeleteSourcingEventMutation();
  const copyOperation = event.operations?.find((operation) => operation.operation === "CopySourcingEvent");
  const { copy: copyProcurement, isCopying, modal: copyModal } = useCopyProcurement();

  const cancelOrClose = useCancelOrCloseProcurement(event);
  const change = useChangeFlow(event);

  const status = event.status;
  const isDraft = status === SourcingEventStatus.Draft;
  const canCancel =
    status === SourcingEventStatus.Published ||
    status === SourcingEventStatus.Evaluation ||
    status === SourcingEventStatus.Evaluated ||
    status === SourcingEventStatus.AwardDecision;
  const canClose = canCancel || status === SourcingEventStatus.Awarded;
  const deadline = publishedBidDeadline(event);
  const deadlinePassed = deadline != null && new Date(deadline).getTime() <= Date.now();
  const canComplete =
    (status === SourcingEventStatus.Published && deadlinePassed) ||
    status === SourcingEventStatus.Evaluation ||
    status === SourcingEventStatus.Awarded;
  const awardStands = (event.award?.winningBidIds?.length ?? 0) > 0;
  const publicAwardStands = awardStands && event.eventContext.type === "Public";
  const awardCreatedAContract = (event.award?.contractIds?.length ?? 0) > 0;
  const canReopen =
    (status === SourcingEventStatus.Evaluated || status === SourcingEventStatus.Completed) && !publicAwardStands;

  // ADR-033 D1: round creation lives here now that negotiation is no longer a tab.
  const { rounds, latestSentRound } = useRoundSelection();
    const hasDraftRound = rounds.some((round) => round.status === NegotiationRoundStatus.Draft);
  const hasFinalRound = rounds.some((round) => round.isFinalRound === true);
  // A buyer who announced a forhandlingsforbehold may award on the initial tenders, but nothing
  // stops them negotiating anyway — the reservation is a right, not a commitment.
  const canCreateRound =
    supportsNegotiationRounds(event) &&
    (status === SourcingEventStatus.Published || status === SourcingEventStatus.Evaluation) &&
    !hasDraftRound &&
    !hasFinalRound;

  const startRound = useStartRound(event);

  const completesEvaluation = status === SourcingEventStatus.Evaluation;
  const { data: evaluationForGate } = useGetEvaluationQuery(
    completesEvaluation ? { eventId: event.id, round: latestSentRound?.id } : skipToken,
  );
  const canCompleteEvaluation = !completesEvaluation || everyScoreIn(evaluationForGate);
  const reopensEvaluation = status === SourcingEventStatus.Evaluated;

  const completingAnAward = status === SourcingEventStatus.Awarded;
  const { isLoading: checkingContracts } = useListBidsForEventQuery(
    completingAnAward ? { eventId: event.id } : skipToken,
  );

  const completionDescription = () => {
    if (completesEvaluation) return completeEvaluationDescription();
    const completion = publicAwardStands
      ? t("The procurement is marked as completed. The award stands, so it cannot be reopened afterwards.")
      : t("The procurement is marked as completed. You can reopen it later.");
    if (!completingAnAward) return completion;
    return (
      <>
        <Text>{completion}</Text>
        <Box mt="3">
          <MissingContractsNotice event={event} />
        </Box>
      </>
    );
  };

  const reopenDescription = () => {
    const sentences = [t("The procurement is moved back to evaluation.")];
    if (!awardStands) return sentences.join(" ");

    sentences.push(
      t("The award is withdrawn and every bid goes back into the running. Suppliers who received an award letter are told."),
    );
    if (awardCreatedAContract) sentences.push(t("A contract that was created from the award is not removed."));
    return sentences.join(" ");
  };

  const designContest = isDesignContest(event.eventContext.type === "Public" ? event.eventContext.procedureType : null);
  const completeEvaluationTitle = designContest ? t("Register the jury's decision?") : t("Complete the evaluation?");
  const completeEvaluationLabel = designContest ? t("Register the jury's decision") : t("Complete the evaluation");

  const handleComplete = () => {
    confirmationModal.show({
      title: completesEvaluation ? completeEvaluationTitle : t("Complete the procurement?"),
      description: completionDescription(),
      onConfirmed: async () => {
        try {
          await completeEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleReopen = () => {
    confirmationModal.show({
      title: reopensEvaluation ? t("Reopen the evaluation?") : t("Reopen the procurement?"),
      description: reopenDescription(),
      onConfirmed: async () => {
        try {
          await reopenEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleCopy = () => copyProcurement(event.id);

  const handleDelete = () => {
    confirmationModal.show({
      title: t("Delete the draft?"),
      description: t("The draft is permanently removed."),
      confirmButtonColor: "red",
      confirmButtonText: t("Delete") ?? "",
      onConfirmed: async () => {
        try {
          await deleteEvent({ eventId: event.id }).unwrap();
          navigate(urls.app.sourcingEvents.viewAll);
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <>
      {confirmationModal.modal}
      {copyModal}
      {startRound.modal}
      {change.draft && change.editor.isOpen && (
        <EventChangeEditor
          key={change.draft.id}
          event={event}
          draft={change.draft}
          isOpen={change.editor.isOpen}
          onClose={change.editor.onClose}
        />
      )}
      {cancelOrClose.modal}

      <Menu>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="outline"
          aria-label={t("Actions") ?? "Actions"}
          icon={<Icon as={FiMoreHorizontal} />}
        />
        <MenuList>
          {(change.canStartChange || change.draft) && (
            <MenuItem onClick={change.draft ? change.openChange : change.startChange} isDisabled={change.isStarting}>
              {change.actionLabel}
            </MenuItem>
          )}
          <Explanation text={copyOperation?.reason ? t(copyOperation.reason.code) : ""} enabled={!copyOperation?.allowed}>
            <MenuItem
              onClick={handleCopy}
              isDisabled={isCopying || !copyOperation?.allowed}
              isFocusable={!copyOperation?.allowed}
            >
              {t("Copy the procurement")}
            </MenuItem>
          </Explanation>
          {canCreateRound && (
            <MenuItem onClick={startRound.start} isDisabled={startRound.isStarting}>
              {t("New round")}
            </MenuItem>
          )}
          {canComplete && (
            <MenuItem onClick={handleComplete} isDisabled={!canCompleteEvaluation || checkingContracts}>
              {completesEvaluation ? completeEvaluationLabel : t("Complete the procurement")}
            </MenuItem>
          )}
          {canReopen && (
            <MenuItem onClick={handleReopen}>
              {reopensEvaluation ? t("Reopen the evaluation") : t("Reopen the procurement")}
            </MenuItem>
          )}
          {canClose && <MenuItem onClick={() => cancelOrClose.open("close")}>{t("Close the procurement")}</MenuItem>}
          {canCancel && (
            <MenuItem color="smError" onClick={() => cancelOrClose.open("cancel")}>
              {t("Cancel the procurement")}
            </MenuItem>
          )}
          {isDraft && (
            <MenuItem color="smError" onClick={handleDelete}>
              {t("Delete the draft")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};
